// ─── Admin Vouchers v2 (payment-integrated / MySQL ffVoucher) ─────────────────

export type VoucherValueType = 'FIXED' | 'PERCENT' | 'NONE';
export type VoucherCategory = 'PAYMENT' | 'DISCOUNT' | 'ACCESS';

/** API list filter status (server enum). */
export type VoucherListStatus = 'active' | 'revoked' | 'redeemed' | 'expired';

/** Client-derived display status. */
export type VoucherDisplayStatus =
  | 'active'
  | 'inactive'
  | 'expired'
  | 'exhausted'
  | 'revoked';

export type VoucherScopeBadge =
  | 'User-specific'
  | 'Product-bound'
  | 'Store'
  | 'Org'
  | 'Scoped'
  | 'General';

export type VoucherScopeType = 'object' | 'entity' | 'entity_type';
export type VoucherScopeMode = 'exact' | 'self' | 'children' | 'descendants';

export interface VoucherScopeInput {
  scope_type: VoucherScopeType;
  ref_id: number;
  mode?: VoucherScopeMode;
}

export interface VoucherScope extends VoucherScopeInput {
  vsid?: number;
}

export interface VoucherScopesResponse {
  vid: number;
  scopes: VoucherScope[];
  legacy_toid_rule?: VoucherScope | null;
  unrestricted: boolean;
}

export interface ReplaceVoucherScopesPayload {
  scopes: VoucherScopeInput[];
}

export interface ReplaceVoucherScopesResponse {
  success: boolean;
  vid: number;
  scopes: VoucherScope[];
}

// ─── Catalog type ─────────────────────────────────────────────────────────────

export interface VoucherType {
  vtid: number;
  name: string;
  name_system: string;
  category: VoucherCategory | string;
  value_type: VoucherValueType | string;
  description?: string | null;
  active?: number | boolean;
  [key: string]: unknown;
}

// ─── Core entity ──────────────────────────────────────────────────────────────

export interface Voucher {
  vid: number;
  ffid: string;
  uuid?: string;
  code: string;
  vtid: number;
  ccode: string;
  value: number;
  min_purchase_amount?: number | null;
  max_discount_amount?: number | null;
  oid_issuer?: number | null;
  storeid?: number | null;
  uid_issuer?: number | null;
  uid_assigned?: number | null;
  toid?: number | null;
  name?: string | null;
  description?: string | null;
  terms_json?: Record<string, unknown> | null;
  date_valid_from?: string | null;
  date_valid_until?: string | null;
  active: number;
  revoked: number;
  redeemed: number;
  redemption_count?: number;
  max_redemptions?: number | null;
  max_redemptions_per_user?: number | null;
  date_added?: string;
  date_modified?: string;
}

export interface VoucherUsage {
  vid: number;
  redemption_count: number;
  max_redemptions: number | null;
  remaining: number | null;
  is_exhausted: boolean;
}

export interface VoucherRedemption {
  vrid: number;
  vid: number;
  uid: number;
  pid?: number | null;
  invid?: number | null;
  amount_applied: number;
  original_amount?: number | null;
  ip_address?: string | null;
  date_redeemed: string;
}

export interface VoucherPagination {
  total: number;
  limit: number;
  offset: number;
  has_more: boolean;
}

// ─── Payloads ─────────────────────────────────────────────────────────────────

export interface CreateVoucherPayload {
  vtid: number;
  value: number;
  code?: string;
  name?: string;
  description?: string;
  ccode?: string;
  oid_issuer?: number;
  storeid?: number;
  uid_assigned?: number;
  /** @deprecated Prefer `scopes` for new vouchers. */
  toid?: number;
  scopes?: VoucherScopeInput[];
  date_valid_from?: string;
  date_valid_until?: string;
  min_purchase_amount?: number;
  max_discount_amount?: number;
  max_redemptions?: number;
  max_redemptions_per_user?: number;
  terms_json?: Record<string, unknown>;
}

export interface UpdateVoucherPayload {
  name?: string | null;
  description?: string | null;
  value?: number;
  date_valid_from?: string | null;
  date_valid_until?: string | null;
  min_purchase_amount?: number | null;
  max_discount_amount?: number | null;
  max_redemptions?: number | null;
  max_redemptions_per_user?: number | null;
  active?: number;
  ccode?: string;
  uid_assigned?: number | null;
}

export interface VoucherListParams {
  oid?: number;
  storeid?: number;
  vtid?: number;
  uid_assigned?: number;
  code?: string;
  status?: VoucherListStatus | '';
  limit?: number;
  offset?: number;
}

// ─── Responses ────────────────────────────────────────────────────────────────

export interface VoucherTypesResponse {
  voucher_types: VoucherType[];
}

export interface VouchersListResponse {
  vouchers: Voucher[];
  pagination: VoucherPagination;
}

export interface VoucherRedemptionsResponse {
  redemptions: VoucherRedemption[];
  pagination: VoucherPagination;
}

export interface VoucherRevokeResponse {
  success: boolean;
  voucher: Voucher;
}

export interface VoucherDeleteResponse {
  success: boolean;
  vid: number;
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

export function deriveVoucherStatus(v: Voucher): VoucherDisplayStatus {
  if (v.revoked === 1) return 'revoked';
  if (v.active === 0) return 'inactive';
  if (v.date_valid_until) {
    const until = new Date(v.date_valid_until).getTime();
    if (!Number.isNaN(until) && until < Date.now()) return 'expired';
  }
  const max = v.max_redemptions;
  const count = v.redemption_count ?? 0;
  if (max != null && max > 0 && count >= max) return 'exhausted';
  if (max == null && v.redeemed === 1) return 'exhausted';
  return 'active';
}

export function scopeBadges(v: Voucher, hasScopes = false): VoucherScopeBadge[] {
  const badges: VoucherScopeBadge[] = [];
  if (v.uid_assigned != null) badges.push('User-specific');
  if (v.toid != null || hasScopes) badges.push(hasScopes ? 'Scoped' : 'Product-bound');
  if (v.storeid != null) badges.push('Store');
  if (v.oid_issuer != null) badges.push('Org');
  if (badges.length === 0) badges.push('General');
  return badges;
}

export function formatScopeLabel(scope: VoucherScopeInput): string {
  const typeLabel =
    scope.scope_type === 'object'
      ? 'Object'
      : scope.scope_type === 'entity'
        ? 'Listing'
        : 'Category';
  const mode =
    scope.scope_type === 'entity_type'
      ? ` · ${scope.mode || 'descendants'}`
      : '';
  return `${typeLabel} #${scope.ref_id}${mode}`;
}

export function formatVoucherValue(
  value: number,
  valueType: string | undefined,
  ccode = 'GHS',
): string {
  const vt = (valueType || 'FIXED').toUpperCase();
  if (vt === 'PERCENT') return `${value}%`;
  if (vt === 'NONE') return 'Access pass';
  return `${ccode} ${Number(value).toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 2 })}`;
}

export function getApiErrorCode(error: unknown): string | undefined {
  const data = (error as { data?: { error?: string } })?.data;
  return data?.error;
}
